import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { LogIn, User, Lock, Shield, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '../components/ui/alert';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { initiateGoogleLogin, processGoogleCallback } from '../utils/googleAuth';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = ({ onLogin, lang }) => {
  const { t } = useTranslation(lang);
  const navigate = useNavigate();
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  // Process Google OAuth callback
  useEffect(() => {
    const handleGoogleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('code')) {
        try {
          const data = await processGoogleCallback();
          if (data) {
            // Check if user is admin
            if (!data.user.is_admin) {
              toast.error('Access denied. Admin privileges required.');
              navigate('/admin/login');
              return;
            }
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));
            onLogin(data.user);
            toast.success('Welcome back, Admin!');
            navigate('/admin');
          }
        } catch (error) {
          toast.error('Google sign-in failed');
        }
      }
    };

    handleGoogleCallback();
  }, [navigate, onLogin]);

  const handleGoogleSignIn = () => {
    initiateGoogleLogin();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      
      // Check if user is admin
      if (!response.data.user.is_admin) {
        toast.error('Access denied. Admin privileges required.');
        setLoading(false);
        return;
      }

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      onLogin(response.data.user);
      toast.success('Welcome back, Admin!');
      navigate('/admin');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid admin credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHead 
        title="Admin Login | AAA of Miami"
        description="Admin login for AAA of Miami dealership"
      />
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center px-4">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-red-600/30 to-orange-600/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-red-600/30 to-orange-600/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-yellow-600/20 to-red-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="max-w-md w-full relative z-10">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-500 to-red-700 rounded-full mb-4 shadow-2xl border-4 border-red-900">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display' }} data-testid="admin-login-title">
            {t('adminLogin.title')}
          </h1>
          <p className="text-red-300 text-lg font-semibold">{t('adminLogin.subtitle')}</p>
          <div className="mt-3 inline-flex items-center space-x-2 text-red-400 text-sm bg-red-950/50 px-4 py-2 rounded-full border border-red-800">
            <Lock className="w-4 h-4" />
            <span>{t('adminLogin.restrictedAccess')}</span>
          </div>
        </div>

        {/* Warning Alert */}
        <Alert className="mb-6 bg-red-950/50 border-red-800 backdrop-blur-sm">
          <AlertTriangle className="h-4 w-4 text-red-400" />
          <AlertDescription className="text-red-300 text-sm">
            {t('adminLogin.warning')}
          </AlertDescription>
        </Alert>

        {/* Login Card */}
        <div className="bg-gray-900/90 backdrop-blur-md rounded-2xl shadow-2xl p-8 border-2 border-red-800/50">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-white mb-1">{t('adminLogin.description')}</h2>
            <p className="text-gray-400 text-sm">{t('adminLogin.enterCredentials')}</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">{t('adminLogin.adminUsername')}</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                <Input
                  type="text"
                  placeholder={t('adminLogin.enterAdminUsername')}
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                  className="pl-12 h-12 text-base bg-gray-800 border-2 border-gray-700 text-white placeholder-gray-500 focus:border-red-600 focus:ring-red-600 rounded-lg"
                  required
                  data-testid="login-username"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">{t('adminLogin.adminPassword')}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                <Input
                  type="password"
                  placeholder={t('adminLogin.enterAdminPassword')}
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  className="pl-12 h-12 text-base bg-gray-800 border-2 border-gray-700 text-white placeholder-gray-500 focus:border-red-600 focus:ring-red-600 rounded-lg"
                  required
                  data-testid="login-password"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold text-base rounded-lg shadow-lg transition-all duration-300 hover:shadow-red-900/50" 
              disabled={loading} 
              data-testid="login-submit"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="spinner w-5 h-5 border-2 border-white border-t-transparent"></div>
                  <span className="ml-2">{t('adminLogin.authenticating')}</span>
                </div>
              ) : (
                <>
                  <Shield className="w-5 h-5 mr-2" />
                  {t('adminLogin.accessPanel')}
                </>
              )}
            </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-900 text-gray-400">
                  Or continue with
                </span>
              </div>
            </div>

            <GoogleSignInButton onClick={handleGoogleSignIn} text="Sign in with Google" />
          </form>

          <div className="mt-6 pt-6 border-t border-gray-700 space-y-3">
            <p className="text-center text-sm text-gray-400">
              {t('adminLogin.needAccess')}{' '}
              <Link to="/admin/register" className="text-red-400 hover:text-red-300 font-medium">
                {t('adminLogin.registerAdmin')}
              </Link>
            </p>
            <p className="text-center text-sm">
              <Link to="/" className="text-gray-500 hover:text-gray-400">
                ← {t('adminLogin.backToSite')}
              </Link>
            </p>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4">
          <p className="text-gray-400 text-xs text-center">
            <Lock className="w-3 h-3 inline mr-1" />
            {t('adminLogin.secureConnection')} • {t('adminLogin.firstUserAdmin')}
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default AdminLogin;
